import React from "react";
import {
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

/**
 * Local helpers to convert between ISO strings and the native datetime-local
 * input value (YYYY-MM-DDTHH:mm, no timezone suffix).
 */
const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const PRESETS = [
  { label: "Senaste timmen", hours: 1 },
  { label: "Senaste 6 timmar", hours: 6 },
  { label: "Senaste 24 h", hours: 24 },
  { label: "Senaste 7 dagarna", hours: 24 * 7 },
  { label: "Senaste 30 dagarna", hours: 24 * 30 },
];

/**
 * Compact date-range control with quick presets. The presets are the fast
 * path for incident response; the datetime inputs are the forensic deep-dive.
 */
export const MovementDateRange = ({
  fromISO,
  toISO,
  onChange,
  activePresetHours = null,
}) => {
  const theme = useTheme();

  const applyPreset = (hours) => {
    const now = new Date();
    const from = new Date(now.getTime() - hours * 3600 * 1000);
    onChange?.({
      fromISO: from.toISOString(),
      toISO: now.toISOString(),
      presetHours: hours,
    });
  };

  const handleFromChange = (e) => {
    onChange?.({
      fromISO: fromLocalInput(e.target.value),
      toISO,
      presetHours: null,
    });
  };

  const handleToChange = (e) => {
    onChange?.({
      fromISO,
      toISO: fromLocalInput(e.target.value),
      presetHours: null,
    });
  };

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{
          color: "#5f6877",
          letterSpacing: "0.12em",
          fontWeight: 600,
          fontSize: "10.5px",
          display: "block",
          mb: 1,
        }}
      >
        Tidsspann
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
        {PRESETS.map((p) => {
          const active = activePresetHours === p.hours;
          return (
            <Chip
              key={p.hours}
              label={p.label}
              onClick={() => applyPreset(p.hours)}
              clickable
              sx={{
                borderRadius: "8px",
                fontWeight: 600,
                fontSize: 12,
                background: active ? theme.palette.primary.main : "#ffffff",
                color: active ? "#ffffff" : "#10324a",
                border: active
                  ? `1px solid ${theme.palette.primary.main}`
                  : "1px solid #e2e5ea",
                transition: "all 160ms ease",
                "&:hover": {
                  background: active
                    ? theme.palette.primary.dark
                    : "rgba(16,50,74,0.06)",
                },
              }}
            />
          );
        })}
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField
          type="datetime-local"
          size="small"
          label="Från"
          value={toLocalInput(fromISO)}
          onChange={handleFromChange}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: 1 }}
        />
        <TextField
          type="datetime-local"
          size="small"
          label="Till"
          value={toLocalInput(toISO)}
          onChange={handleToChange}
          InputLabelProps={{ shrink: true }}
          sx={{ flex: 1 }}
        />
      </Stack>
    </Box>
  );
};

export default MovementDateRange;
